// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { AGENT_CASES_PATH } from "@src/appV2/ExperimentalHomeHealth/AgentCases/paths";
import { AGENT_VISITS_PATH } from "@src/appV2/ExperimentalHomeHealth/AgentVisits/paths";
import {
  HOME_HEALTH_CASES_PATH,
  HOME_HEALTH_CASE_MOBILE_DETAIL_PATH,
} from "@src/appV2/ExperimentalHomeHealth/Cases/paths";
import { HOME_HEALTH_VISIT_VERIFICATION_PATH } from "@src/appV2/ExperimentalHomeHealth/VisitVerification/paths";
import { HELP_CENTER_FULL_PATH } from "@src/appV2/HelpCenter/paths";
import { CONNECT_EMR_PATH } from "@src/appV2/redesign/HomeHealth/ConnectEmr/paths";
import { COVERAGE_MAP_PATH } from "@src/appV2/redesign/HomeHealth/CoverageMap/paths";
import { HOME_HEALTH_VISIT_VERIFICATION_MOBILE_PATH } from "@src/appV2/redesign/HomeHealth/VisitVerification/paths";
import { type SettingsSection } from "@src/appV2/redesign/Settings/types";
import { type LocationDescriptorObject } from "history";
import * as pathToRegexp from "path-to-regexp";
import queryString from "query-string";

export type Location = LocationDescriptorObject<Record<string, unknown>>;

/**
 * Only use for Route component
 */
export const routes = {
  // generic
  root: "/",
  enter: "/enter",
  login: "/login",
  v2LoginWithEmailPassword: "/v2/login-with-email-password",
  v2ResetPassword: "/v2/reset-password",
  admin: "/admin",
  myAccount: "/my-account",
  myAccountPasskeys: "/my-account/passkeys",
  appV2: "/v2",
  appDownload: "/download",

  // facility
  facilityDashboard: "/facility/dashboard",
  calendarClassic: "/calendar-classic",
  facilityOnboarding: "/facility/onboarding",
  facilityLiveWorkerETA: "/facility/live-worker-eta",
  facilityUsers: "/facility/users",
  facilityWorkerInstructions: "/facility/worker-instructions",
  facilityChat: "/facility/chat",
  facilityChatWithAgent: "/facility/chat/:facilityId?/:agentId?/:placementId?",
  facilityInvoiceHistory: "/facility/invoice/history",
  facilityInvoiceDispute: "/facility/invoice/dispute/:invoiceId",
  facilityInvoiceDisputeRedirect: "/facility/invoice/dispute/:invoiceNumber/redirect",
  facilityInvoiceUpcomingCharges: "/facility/invoice/upcoming-charges",
  facilityInvoiceReporting: "/facility/invoice/reporting",
  facilityInvoiceContractTerms: "/facility/invoice/contract-terms",
  facilityProfile: "/facility/profile",
  facilitySettings: "/facility/settings",

  // workplace
  workplaceGetAccess: "/workplace/get-access",
  workplaceReferrals: "/workplace/get-access/referrals",
  workplaceSignContract: "/workplace/:workplaceId/sign-contract",
  workplaceWorkers: "/workplace/workers",
  workplaceWorkerDetails: "/workplace/workers/:workerId",
  workersDirectory: "/workplace/workers-directory",
  workersDirectoryWorkerDetails: "/workplace/workers-directory/:workerId",
  workplaceUsers: "/workplace/users",
  workplaceUserDetails: "/workplace/users/:userId",
  workplaceBlockShifts: "/workplace/block-shifts",
  workplaceReferAndEarn: "/workplace/refer-and-earn",
  workplaceHireProfessionals: "/workplace/hire-professionals",
  workplaceHireProfessionalDetails: "/workplace/hire-professionals/:placementCandidateAccessId",
  jobsLandingPage: "/workplace/jobs-landing-page",
  manageJobs: "/workplace/manage-jobs",
  interviewSettings: "/workplace/interview-availability",
  interviewSchedule: "/workplace/interview-schedule",
  workplaceDocumentMapping: "/workplace/required-documents",
  workplaceCharting: "/workplace/charting",
  workplaceAnalytics: "/workplace/analytics",
  workplaceAnalyticsCorporate: "/corporate/analytics",
  corporateSpendingControls: "/corporate/spending-controls",
  workplaceSpendingControls: "/workplace/spending-controls",
  workplaceInternalRoster: "/workplace/internal-roster",
  workplaceJobApplicantDetails:
    "/workplace/candidate/:placementCandidateAccessId/application/:placementApplicationId",
  publicWorkplacePlacements: "/public/placements/workplace/:workplaceId",
  homeHealthCases: HOME_HEALTH_CASES_PATH,
  homeHealthCaseMobileDetail: HOME_HEALTH_CASE_MOBILE_DETAIL_PATH,
  homeHealthMobileUnsupported: "/home-health/mobile-unsupported",
  homeHealthVisitVerification: HOME_HEALTH_VISIT_VERIFICATION_PATH,
  homeHealthVisitVerificationMobile: HOME_HEALTH_VISIT_VERIFICATION_MOBILE_PATH,
  homeHealthCoverageMap: COVERAGE_MAP_PATH,
  homeHealthConnectEmr: CONNECT_EMR_PATH,
  helpCenter: HELP_CENTER_FULL_PATH,

  // workers
  workers: "/workers",
  document: "/documents/:userId",
  agentDashboard: "/agent/dashboard",
  agentDnr: "/agent/dnr",
  agentPayroll: "/agent/payroll",
  agentProof: "/agent/proof",
  agentStripePayouts: "/agent/stripe-payouts",
  agentStripeTransfers: "/agent/stripe-transfers",
  agentAttendance: "/agent/attendance",
  agentProfile: "/agent/profile",
  agentICAgreement: "/agent/:workerId/ic-agreement/:icAgreementVersion/download",
  agentPlacement: "/agent/placement",
  agentPlacementCandidateExclusions: "/agent/placement-candidate-exclusions",
  agentCases: AGENT_CASES_PATH,
  agentVisits: AGENT_VISITS_PATH,

  // redesigned chat
  chat: "/chat",
  chatWithConversation: "/chat/:workplaceId?/:workerId?/:placementId?",

  adminSmsPortal: "/admin/sms-portal",
  adminSmsPortalConversation: "/admin/sms-portal/:phone",

  settings: "/settings",

  calendar: "/calendar",
  schedule: "/schedule",
  dailyView: "/dailyView",
  reference: "/reference/:id",
  timesheetRedirect: "/timesheet/:invoiceNumber/redirect",
  timesheet: "/timesheet/:id",
  signatorySign: "/signatorySign/:facilityId/:signatoryId",
  shiftDetails: "/shift-details/:shiftId",

  verifyDocument: "/verify-document",

  // admin
  adminShiftVerification: "/admin/shift-verification",
  adminShiftVerificationShifts: "/admin/shift-verification/shifts",
  adminShiftVerificationFacilities: "/admin/shift-verification/facilities",
  adminShiftVerificationShiftCreate: "/admin/shift-verification/shift-create",
  adminShiftVerificationExtras: "/admin/shift-verification/extras",
  adminShiftVerificationManual: "/admin/shift-verification/manual-shift-verification",
  adminShifts: "/admin/shifts",
  adminTimeclock: "/admin/timeclockrequests",
  adminProfile: "/admin/profile",
  adminInvoice: "/admin/invoice/:id",
  adminInvoicePdf: "/admin/invoice/pdf/:id",
  adminInvoiceDispute: "/admin/post-invoice-dispute/:invoiceId",
  adminPreInvoiceDispute: "/admin/pre-invoice-dispute/:invoiceId/:invoiceItemId",
  adminAccounts: "/admin/accounts",
  adminAccountsAgents: "/admin/accounts/agents",
  adminAccountsEmployees: "/admin/accounts/employees",
  adminAccountsInvocingByPeriod: "/admin/accounts/invoicing/byperiod",
  adminAccountsInvocingByNumber: "/admin/accounts/invoicing/bynumber",
  adminAccountsPostInvoiceDisputes: "/admin/accounts/invoicing/post-invoice-disputes",
  adminAccountsPreInvoiceDisputes: "/admin/accounts/invoicing/pre-invoice-disputes",
  adminAccountsFacilities: "/admin/accounts/facilities",
  adminAccountsRequirement: "/admin/accounts/requirements",
  adminAccountsLocations: "/admin/accounts/locations",
  adminAccountsChangeLog: "/admin/accounts/changelog",
  adminAccountsExclusions: "/admin/accounts/exclusions",
  adminAccountsPlacementCandidateBans: "/admin/accounts/hiring-bans",
  adminAccountsInterviews: "/admin/accounts/interviews",
  adminAccountsCommentReviews: "/admin/accounts/comment-reviews",
  adminAccountsExternalWorkplacePhotosScraping:
    "/admin/accounts/external-workplace-photos-scraping",
  adminLogs: "/admin/logs",
  adminLogsShift: "/admin/logs/shift",
  adminShiftPayments: "/admin/shift-payments",
  adminShiftPaymentsPayRates: "/admin/shift-payments/pay-rates",
  adminDocument: "/admin/documents/:userId",
  adminUrgentShiftNcns: "/admin/urgentShiftsNcns",
  adminManageWorkplaceTypes: "/admin/workplace-types",
  adminManageQualifications: "/admin/qualifications",
  adminQRCode: "/admin/qr-code",
  shiftSignoutFeedback: "/shift-signout-feedback",
  adminToolsTimekeepingVideos: "/admin/tools/timekeeping-videos",
  adminToolsShiftExplainer: "/admin/tools/shift-explainer",
  adminHiringScraping: "/admin/hiring/scraping",
  adminToolsBadgeVerificationGrading: "/admin/tools/badge-verification-grading",
  adminToolsSsnWorkerLookup: "/admin/tools/ssn-worker-lookup",
  adminToolsEligibility: "/admin/tools/eligibility",
  adminToolsEvidences: "/admin/tools/evidences",
  adminToolsHomeHealthWorkerLookup: "/admin/tools/home-health-worker",
  adminPlacementsAvailability: "/admin/placements/availability",
  shiftBoard: "/shift-board",
} as const;

export function getLocation(name: keyof typeof routes): Location;
export function getLocation(
  name: "facilityWorkerInstructions",
  options?: {
    queryParams?: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      click_src:
        | "onboarding"
        | "calendar_with_quiz_present"
        | "calendar_with_quiz_not_present"
        | "dnr"
        | "rating"
        | "menu_item";
    };
  }
): Location;
export function getLocation(
  name: "enter",
  options?: {
    queryParams?: {
      agentId?: string;
      url?: string;
      selfSignUpUserEmail?: string;
    };
  }
): Location;
export function getLocation(
  name: "workplaceGetAccess",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  options?: { queryParams?: { "referral-code": string; "referral-intent"?: string } }
): Location;
export function getLocation(
  name: "workplaceReferrals",
  options?: { queryParams?: { referrerEmail: string } }
): Location;
export function getLocation(
  name: "workplaceReferAndEarn",
  options: { queryParams: { source: "banner" | "sidenav" } }
): Location;
export function getLocation(
  name: "agentDashboard" | "facilityDashboard" | "calendarClassic",
  options?: { queryParams?: { date: string } }
): Location;
export function getLocation(
  name: "facilityChatWithAgent",
  options: { pathParams: { facilityId: string; agentId: string; placementId?: string } | undefined }
): Location;
export function getLocation(
  name: "chatWithConversation",
  options: {
    pathParams: { workplaceId: string; workerId: string; placementId?: string };
  }
): Location;
export function getLocation(
  name: "shiftSignoutFeedback",
  options: { queryParams: { shortLink: string } }
): Location;
export function getLocation(
  name: "workplaceWorkerDetails" | "workersDirectoryWorkerDetails",
  options: { pathParams: { workerId: string } }
): Location;
export function getLocation(
  name: "workplaceAnalytics",
  options: { queryParams: { embedId: string } }
): Location;
export function getLocation(
  name: "document" | "workplaceUserDetails" | "adminDocument",
  options: { pathParams: { userId: string } }
): Location;
export function getLocation(
  name: "agentICAgreement",
  options: { pathParams: { workerId: string; icAgreementVersion: number } }
): Location;
export function getLocation(
  name: "workplaceSignContract",
  options: { pathParams: { workplaceId: string } }
): Location;
export function getLocation(
  name: "reference" | "adminInvoice" | "adminInvoicePdf" | "timesheet",
  options: { pathParams: { id: string } }
): Location;
export function getLocation(
  name: "timesheetRedirect",
  options: { pathParams: { invoiceNumber: number } }
): Location;
export function getLocation(
  name: "signatorySign",
  options: { pathParams: { facilityId: string; signatoryId: string } }
): Location;
export function getLocation(
  name: "shiftDetails",
  options: { pathParams: { shiftId: string } }
): Location;
export function getLocation(
  name: "workplaceWorkers",
  options: {
    queryParams:
      | Partial<{
          category: "upcoming" | "in_progress" | "past";
          search: string;
          workerProfileDrawerId: string;
        }>
      | undefined;
  }
): Location;

export function getLocation(
  name: "settings" | "facilitySettings",
  options: {
    queryParams: {
      tab: SettingsSection;
      source?: "calendar-setting-click";
    };
  }
): Location;

export function getLocation(
  name: "workplaceHireProfessionals",
  options: { queryParams: { source: "browse-professionals-alert" | "sidenav" } }
): Location;

export function getLocation(
  name: "workplaceHireProfessionalDetails",
  options: { pathParams: { placementCandidateId: string } }
): Location;

export function getLocation(
  name: "manageJobs" | "interviewSettings" | "interviewSchedule",
  options: { queryParams: { source: "sidenav" } }
): Location;

export function getLocation(
  name: "manageJobs",
  options: { queryParams: { searchApplicant?: string } }
): Location;

export function getLocation(
  name: "workplaceJobApplicantDetails",
  options: { pathParams: { placementCandidateAccessId: string; placementApplicationId: string } }
): Location;

export function getLocation(
  // eslint-disable-next-line @typescript-eslint/unified-signatures
  name: "publicWorkplacePlacements",
  options: { pathParams: { workplaceId: string } }
): Location;

// TODO: Remove the overload mechanism and use PATH constants instead along with native react-router methods such as `generatePath`
export function getLocation(
  name: keyof typeof routes,
  {
    pathParams,
    queryParams,
  }:
    | Partial<{
        pathParams?: Record<string, unknown>;
        queryParams?: Record<string, unknown>;
      }>
    | undefined = {}
): Location {
  const pathname = routes[name] ?? "";
  const location: Location = {
    pathname,
  };

  if (pathParams) {
    const pathFunction = pathToRegexp.compile(pathname);
    location.pathname = pathFunction(pathParams);
  }

  if (queryParams) {
    // FIXME: Automatically disabled during ESLint config update
    const query = queryString.stringify(queryParams, {
      arrayFormat: "bracket",
    });
    location.search = query;
  }

  return location;
}

/**
 * Builds a worker deep link for the admin app (the agent dashboard drawer).
 * Admin callers must use this instead of the workplace-facing
 * `/workplace/workers/:workerId` route, which is gated by `isWorkplace` and
 * silently redirects logged-in admins.
 */
export function getAdminWorkerDeepLinkPath(workerId: string): string {
  return `/agent/dashboard?userId=${encodeURIComponent(workerId)}&drawer=Agent`;
}
